import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup, Grid, InputAdornment, Radio, TextField, Typography } from "@mui/material";
import { useQuery } from "react-query";
import { IDataProdutoNF } from ".";
import { useFormContext } from "react-hook-form";
import axiosClient from "../../../../../../../lib/axios";
import PaperDraggable from "../../../../../../../components/PaperDraggable";
import SearchIcon from "@mui/icons-material/Search";
import { useCallback, useMemo, useRef, useState } from "react";
import useDebounce from "../../../../../../../lib/useDebounce";
import DisplayNotFoundInfo from "../../../../../../../components/DisplayNotFindInfo";
import BtnCarregarDados from "../../../../../../../components/BtnCarregarDados";
import { formatarValorMonetario } from "../../../../../../../helpers/masks";
import moment from "moment";

export default function DialogSelecionarProdutoOcs({
  open,
  produto,
  onClose
}: DialogSelecionarProdutoOcsProps) {

  //formulario
  const { getValues, setValue } = useFormContext()
  const notaFiscal = getValues("notaFiscal")

  const [limit, setLimit] = useState(20);
  const [palavraChave, setPalavraChave] = useState("");
  const [selecionado, setSelecionado] = useState<TDataProdutoOC | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedKeyword = useDebounce(palavraChave);
  //querys e mutations 
  const { data, isFetching, isLoading } = useQuery({
    queryKey: ["pesquisar-ordem-compra-itens", debouncedKeyword, limit],
    queryFn: () =>
      axiosClient.get(`/estoque/entrada-mercadorias/pesquisar-ordem-compra-itens/${notaFiscal.id}`, {
        params: {
          pesquisar: debouncedKeyword,
          limit
        }
      }),
    cacheTime: 0,
    staleTime: 0,
    onSuccess: () => {
    },
  });

  const { requestOcItens, dataOcItens } = useMemo(
    () => ({
      requestOcItens: data?.data,
      dataOcItens: data?.data.data,
    }),
    [data]
  );

  const handleClick = useCallback(
    (item: any) => {
      if (selecionado?.id === item.id) {
        setSelecionado(null);
        return;
      }
      setSelecionado(item);
    },
    [selecionado]
  );

  const handleSubmit = useCallback(() => {
    const produtosNF: IDataProdutoNF[] = getValues("produtosNF") || [];
    if (selecionado) {
      const updatedProdutosNF = produtosNF.map((p) => {
        if (p.nf_item_id === produto.nf_item_id && p.nf_produto === produto.nf_produto) {
          return {
            ...p,
            oci_id: selecionado.id,
            numero_ordem_compra: selecionado.numero_ordem_compra,
            oc_nome_produto: selecionado.produto_nome,
            oc_valor: selecionado.valor_unitario,
            oc_quantidade: selecionado.quantidade,
            oc_unidade_medida: selecionado.oc_unidade_medida,
            status: 'Encontrado'
          };
        }
        return p;
      });
      setValue("produtosNF", updatedProdutosNF);

      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecionado]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperComponent={PaperDraggable}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Selecione o produto correspondente</DialogTitle>
      <DialogContent>
        <Box sx={{ bgcolor: '#f5f5f5' }} p={2} mb={2}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" fontWeight={'bold'}>Produto</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {produto.nf_produto}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" fontWeight={'bold'}>Quantidade</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {produto.nf_quantidade} {produto.oc_unidade_medida}
              </Typography>
            </Grid>
            {
              produto.nf_lote &&
              <Grid size={{ xs: 12 }}>
                <TextField
                  multiline
                  fullWidth
                  label="Observações"
                  value={`Lote: ${produto.nf_lote} | Data de fabricação: ${produto.nf_data_fabricacao_lote ? moment(produto.nf_data_fabricacao_lote).format('DD/MM/YYYY') : 'N/A'} | Data de validade: ${produto.nf_data_validade_lote ? moment(produto.nf_data_validade_lote).format('DD/MM/YYYY') : 'N/A'}`}
                  slotProps={{ input: { readOnly: true } }}
                  variant="outlined"
                />
              </Grid>
            }
          </Grid>
        </Box>
        <Box display="flex" alignItems="center" gap={2} py={2}>
          <TextField
            value={palavraChave}
            fullWidth
            inputRef={inputRef}
            label="Pesquisar"
            slotProps={{
              htmlInput: { sx: { textTransform: "uppercase", tabIndex: 0 } },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }
            }}
            onChange={(e) => setPalavraChave(e.target.value)}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {isLoading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && (
          <FormGroup>
            <Box
              display="flex"
              overflow="auto"
              height={{ xs: 550, md: 350 }}
              flexDirection="column"
            >
              {dataOcItens && dataOcItens.length <= 0 && (
                <DisplayNotFoundInfo label="Nenhum produto encontrado" />
              )}

              {dataOcItens &&
                dataOcItens.length > 0 &&
                dataOcItens.map((i: TDataProdutoOC, index: number) => {
                  const isChecked = selecionado?.id === i.id;
                  return (
                    <FormControlLabel
                      key={index}
                      onChange={() => handleClick(i)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleClick(i);
                        }
                      }}
                      control={
                        <Radio checked={isChecked} />
                      }
                      sx={{ mb: 2, alignItems: 'flex-start' }}
                      label={
                        <Box sx={{ width: '100%', ml: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={600} variant="body1">
                              {`${i.produto_id || 'N/A'} - ${i.produto_nome || 'N/A'}`}
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="text.secondary">
                            {`Valor unitário: ${formatarValorMonetario(i.valor_unitario)}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" mb={0.5}>
                            OC - {i.numero_ordem_compra || 'N/A'} / {i.quantidade || 'N/A'} {i.oc_unidade_medida || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" mb={0.5}>
                            Data: {i.data_item ? moment(i.data_item).format('DD/MM/YYYY') : 'N/A'}
                          </Typography>
                        </Box>
                      }
                    />
                  );
                })}
              <BtnCarregarDados
                {...{ limit, isFetching }}
                total={requestOcItens.total}
                action={() => setLimit((prevState) => prevState + 20)}
              />
            </Box>
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button onClick={handleSubmit} variant="contained">Selecionar</Button>
      </DialogActions>

    </Dialog>
  )
}

interface DialogSelecionarProdutoOcsProps {
  open: boolean;
  produto: IDataProdutoNF
  onClose: () => void;
}

type TDataProdutoOC = {
  id: string;
  numero_ordem_compra: number;
  produto_id: string;
  produto_nome: string;
  quantidade: number;
  oc_unidade_medida: string;
  valor_unitario: number;
  data_item: string | null;
}